import React, { useState, useEffect } from 'react';
import { Shield } from 'lucide-react';

const PRIVACY_ACCEPTED_KEY = 'vesselmind_privacy_accepted';

const PrivacyNotice = ({ onAccept }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const accepted = localStorage.getItem(PRIVACY_ACCEPTED_KEY);
    if (!accepted) setVisible(true);
  }, []);

  const handleAccept = () => {
    localStorage.setItem(PRIVACY_ACCEPTED_KEY, new Date().toISOString());
    setVisible(false);
    if (onAccept) onAccept();
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-pvs-card border border-pvs-border rounded-xl shadow-pvs-glow max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-pvs-accent rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-pvs-bg" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">VesselMind Privacy Notice</h2>
              <p className="text-xs text-pvs-text-dim">Data protection information</p>
            </div>
          </div>

          <div className="space-y-4 text-sm text-pvs-text-muted">
            <section>
              <h3 className="font-semibold text-white mb-1">What data we collect</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Your questions and the responses provided</li>
                <li>An anonymous session identifier (not linked to your identity)</li>
                <li>Confidence scores and response timing for quality assurance</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-white mb-1">What we do not collect</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Your name, email address, or IP address</li>
                <li>Browser fingerprints or tracking cookies</li>
                <li>Any personal identification information</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-white mb-1">How your data is used</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>To provide regulatory guidance from LPG Codes of Practice</li>
                <li>To improve the accuracy and coverage of the knowledge base</li>
                <li>For quality assurance and system reliability monitoring</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-white mb-1">Third-party processing</h3>
              <p>Query text may be processed by OpenAI (for semantic search) and Anthropic (for AI-generated responses when no pre-verified answer exists). No personal identifiers are sent to these services. Only the text of your question is transmitted.</p>
            </section>

            <section>
              <h3 className="font-semibold text-white mb-1">Data retention</h3>
              <p>Query logs are automatically deleted after 180 days. You can request immediate deletion of your session data at any time.</p>
            </section>

            <section>
              <h3 className="font-semibold text-white mb-1">Your rights</h3>
              <p>Under UK GDPR, you have the right to access, correct, and delete your data. You can export or erase your session data using the data management option in the chat interface.</p>
            </section>

            <section>
              <h3 className="font-semibold text-white mb-1">Important disclaimer</h3>
              <p>VesselMind is a decision support tool. It does not make compliance determinations and does not replace professional assessment by a competent person. All outputs require professional validation before application.</p>
            </section>
          </div>

          <div className="mt-6 flex gap-3">
            <button
              onClick={handleAccept}
              className="flex-1 py-2.5 bg-pvs-accent text-pvs-bg font-semibold rounded-lg hover:bg-pvs-accent-dim transition text-sm"
            >
              I Understand — Continue
            </button>
          </div>

          <p className="text-xs text-pvs-text-dim text-center mt-3">
            By continuing, you acknowledge you have read and understood this privacy notice.
          </p>
        </div>
      </div>
    </div>
  );
};

export const hasAcceptedPrivacy = () => {
  return !!localStorage.getItem(PRIVACY_ACCEPTED_KEY);
};

export const resetPrivacyAcceptance = () => {
  localStorage.removeItem(PRIVACY_ACCEPTED_KEY);
};

export default PrivacyNotice;
